import React, {useEffect, useState} from 'react';
import {useIntl} from 'react-intl';

import Skeleton from 'coreComponents/Skeleton';
import {useCmsContent, useLocalesMgr} from 'coreHooks';

import {useGeneralProvider} from 'brand/app/components/_app-config/GeneralProvider';
import AccordionGroup from 'brandComponents/core/AccordionGroup';
import {EAccordionGroupStyle} from 'brandComponents/core/AccordionGroup/IAccordionGroup';
import {AccordionGroupItem} from 'brandComponents/core/AccordionGroup/SubComponents';
import ProductDescription from 'brandComponents/ProductDescription';
import SupplierCode from 'brandComponents/SupplierCode';

import {IProductAccordionProps} from './IProductAccordion';
import messages from './messages';
import {ProductLongDescription} from './Subcomponents';

import styles from './ProductAccordion.module.scss';

const ProductAccordion = ({product, contentfulIDs = null, hTag}: IProductAccordionProps) => {
	const {managerRepository} = useGeneralProvider();
	const [contentfulLoaded, setContentfulLoaded] = useState(false);
	const [dynamicContents, setDynamicContents] = useState([]);
	const isPreviewMode = managerRepository.getContentMgr().isPreviewMode();
	const localesMgr = useLocalesMgr();
	const currentLocale = localesMgr.getCurrentLocale();
	const currentLanguage = currentLocale.getLanguageCode();
	const intl = useIntl();

	const DescriptionStaticTab = (
		<AccordionGroupItem
			key={`static-tab-description-${currentLanguage}`}
			title={intl.formatMessage(messages.productDescriptionTitle)}
			content={
				<div className={styles['product-accordion__details']}>
					<ProductDescription text={product?.getShortDescription()} />
				</div>
			}
		/>
	);

	const DetailsStaticTab = (
		<AccordionGroupItem
			key={`static-tab-details-${currentLanguage}`}
			title={intl.formatMessage(messages.productDetailsTitle)}
			content={
				<ul className={styles['product-accordion__details']}>
					<ProductLongDescription description={product.getLongDescription()} />
					{!!product.getSupplierCode() && (
						<li>
							<SupplierCode supplierCode={product.getSupplierCode()} />
						</li>
					)}
				</ul>
			}
		/>
	);

	const staticContents = [DescriptionStaticTab, DetailsStaticTab];

	const {data: accordionData, isLoading: accordionLoading} = useCmsContent({
		contentType: 'accordions',
		config: {
			variables: {
				ids: contentfulIDs,
			},
		},
		collectionKey: 'accordionCollection',
		enabled: Boolean(contentfulIDs?.length),
		deps: [isPreviewMode, currentLanguage, contentfulIDs?.join('.')],
	});

	useEffect(() => {
		if (accordionData?.accordionCollection?.items) {
			const cmsProvider = managerRepository.getContentMgr().getCMSProvider();
			const cmsDataBuilder = cmsProvider.getBuilder();
			const items = accordionData.accordionCollection.items;

			if (Array.isArray(items) && items.length > 0) {
				items.sort((a, b) => {
					const idxA = contentfulIDs.indexOf(a?.internalTitle);
					const idxB = contentfulIDs.indexOf(b?.internalTitle);

					return idxA < idxB ? -1 : idxA > idxB ? 1 : 0;
				});

				setDynamicContents(items.map((b) => cmsDataBuilder.build(b)));
			}

			setContentfulLoaded(true);
		} else if (!contentfulIDs) {
			setDynamicContents([]);
		} else if (!accordionLoading) {
			setDynamicContents([]);
			setContentfulLoaded(true);
		}
	}, [accordionData, accordionLoading, contentfulIDs, managerRepository]);

	return (
		<>
			<AccordionGroup
				activeIndex={0}
				hTag={hTag}
				classNames={styles['product-accordion__group']}
				style={EAccordionGroupStyle.AnimatedCaret}
				allowMultipleOpen
			>
				{[...staticContents, ...dynamicContents]}
			</AccordionGroup>
			{!contentfulLoaded && contentfulIDs?.map((id) => <Skeleton key={id} height="40px" />)}
		</>
	);
};

export default ProductAccordion;
